'use client';

import { type LayoutNode, type LayoutNodeStyleMap } from 'shop-ui/types';
import { ELEMENT_TYPE, FEATURE_FLAG_KEYS } from 'shop-ui/constants';
import { getElementUlId } from 'app/shop-ui/tools/getElementUlId';
import { combineComponent } from 'shop-ui/tools/combineComponent';
import { GRID_LAYOUT_TYPE } from 'shop-ui/section/Grid/rules';
import i18n from '@shared/i18n';

export const componentName = 'ProductInfoGroup';

export const productInfoGroupTypeKey = {
    standard: 'standard',
    feature: 'feature',
} as const;

// 官方 preset 的 key
export enum OFFICIAL_PRODUCT_INFO_GROUP_KEY {
    PRODUCT_INFO_GROUP = 'official-product-info-group',
}

// 官方 preset 的設定
export const showCutList = [
    {
        key: OFFICIAL_PRODUCT_INFO_GROUP_KEY.PRODUCT_INFO_GROUP,
        type: ELEMENT_TYPE.PRODUCT_INFO_GROUP,
        content: '',
    },
];

const config: LayoutNode = {
    id: '',
    type: ELEMENT_TYPE.PRODUCT_INFO_GROUP,
    name: componentName,
    enable: true,
    next: null,
    deletable: false,
};

export const features = [
    FEATURE_FLAG_KEYS.PRODUCT_INFO_GROUP_TYPE,
    FEATURE_FLAG_KEYS.FONT_FAMILY,
    FEATURE_FLAG_KEYS.FONT_WEIGHT,
    FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM,
    FEATURE_FLAG_KEYS.FONT_COLOR,
    FEATURE_FLAG_KEYS.TEXT_ALIGNMENT,
    FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING,
    FEATURE_FLAG_KEYS.COMPONENT_PRIMARY_BUTTON_BG_COLOR,
    FEATURE_FLAG_KEYS.COMPONENT_PRIMARY_BUTTON_TEXT_COLOR,
    FEATURE_FLAG_KEYS.COMPONENT_SECONDARY_BUTTON_BG_COLOR,
    FEATURE_FLAG_KEYS.COMPONENT_SECONDARY_BUTTON_TEXT_COLOR,
    FEATURE_FLAG_KEYS.COMPONENT_DISCOUNT_BADGE_BG_COLOR,
    FEATURE_FLAG_KEYS.COMPONENT_DISCOUNT_BADGE_TEXT_COLOR,
    FEATURE_FLAG_KEYS.ANIMATION,
];

const defaultStyle = {
    [FEATURE_FLAG_KEYS.PRODUCT_INFO_GROUP_TYPE]: 'standard',
    [FEATURE_FLAG_KEYS.FONT_FAMILY]: 'Inter',
    [FEATURE_FLAG_KEYS.FONT_WEIGHT]: '400',
    [FEATURE_FLAG_KEYS.FONT_COLOR]: 'rgba(0, 0, 0, 1)',
    [FEATURE_FLAG_KEYS.TEXT_LETTER_SPACING]: '0px',
    [FEATURE_FLAG_KEYS.TEXT_LINE_HEIGHT]: '1.3',
    [FEATURE_FLAG_KEYS.TEXT_ALIGNMENT]: 'left',
    [FEATURE_FLAG_KEYS.FONT_SIZE]: 'custom',
    [FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM]: '14',
    [FEATURE_FLAG_KEYS.COMPONENT_PRIMARY_BUTTON_BG_COLOR]: 'rgba(33, 32, 38, 1)',
    [FEATURE_FLAG_KEYS.COMPONENT_PRIMARY_BUTTON_TEXT_COLOR]: 'rgba(255, 255, 255, 1)',
    [FEATURE_FLAG_KEYS.COMPONENT_SECONDARY_BUTTON_BG_COLOR]: 'rgba(255, 255, 255, 1)',
    [FEATURE_FLAG_KEYS.COMPONENT_SECONDARY_BUTTON_TEXT_COLOR]: 'rgba(33, 32, 38, 1)',
    [FEATURE_FLAG_KEYS.COMPONENT_DISCOUNT_BADGE_BG_COLOR]: 'rgba(241, 108, 93, 1)',
    [FEATURE_FLAG_KEYS.COMPONENT_DISCOUNT_BADGE_TEXT_COLOR]: 'rgba(254, 237, 236, 1)',
} as const;

// 只是範例，請依照需求調整
export function createProductInfoGroup(presetStyle?: LayoutNodeStyleMap[string]) {
    const id = getElementUlId();

    const layout = {
        ...config,
        id,
    };

    return {
        id,
        layout,
        style: {
            ...defaultStyle,
            ...presetStyle,
        },
    };
}

export function generate(
    sectionName: string = i18n.gettext('區塊'),
    options?: { shouldWrap?: boolean }
) {
    const productInfoGroup = createProductInfoGroup();

    const shouldWrap = options?.shouldWrap ?? true;

    return combineComponent(sectionName, [productInfoGroup], {
        primaryGridType: GRID_LAYOUT_TYPE.SPLIT_H_2C,
        secondaryGridType: GRID_LAYOUT_TYPE.SINGLE_CELL,
        shouldWrap,
    });
}
